import React, { useEffect, useMemo } from "react";
import { LoadingScreen } from "@UI/Components/LoadingScreen/LoadingScreen";
import { useCurrentBasket } from "@modules/Purchasing/hooks/useCurrentBasket";
import { Routes } from "@Libs/Router/Components/Routes/Routes";
import { Route } from "@Libs/Router/Components/Route/Route";
import { ChooseSubscriptionPlan } from "@modules/Subscriptions/Flows/Subscribe/ChooseSubscriptionPlan/ChooseSubscriptionPlan";
import { Navigate } from "@Libs/Router/Components/Navigate/Navigate";
import {
  getSubscriptionPeriod,
  isSubscriptionProduct,
} from "@modules/Subscriptions/services/subscriptionPlans";
import { useFeatureFlag } from "@Libs/FeatureFlags/hooks/useFeatureFlag";
import { isEmpty } from "lodash-es";
import messages from "./messages";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import { Box } from "@UI/Components/Box/Box";
import { useSearchParams } from "@Libs/Router/hooks/useSearchParams";
import { withFlow } from "@Libs/Flow/hocs/withFlow";
import { createFlow } from "@Libs/Flow/services/createFlow";
import { ISubscriptionPlan } from "@modules/Subscriptions/types";
import { Checkout } from "@modules/Purchasing/Flows/Checkout/Checkout";
import { useFlow } from "@Libs/Flow/hooks/useFlow";
import {
  PaymentMethodProviderCombination,
  PaymentMethodType,
  IPaymentProviderForMethod,
} from "@modules/PaymentProcessing/types";
import { GenericAlert } from "@UI/Components/GenericAlert/GenericAlert";
import { useNavigate } from "@Libs/Router/hooks/useNavigate";

export type SubscribeSuccessPayload = {
  subscriptionPlan: ISubscriptionPlan;
  isPaymentConfirmed: boolean;
} & PaymentMethodProviderCombination<PaymentMethodType>;

export const SubscribeFlow = createFlow<
  SubscribeSuccessPayload,
  { relatedServiceId?: number }
>({
  root: "SUBSCRIBE",
  state: {
    relatedServiceId: undefined,
  },
});

export type ISubscribeFlowRoute =
  | "CHOOSE_PLAN"
  | "CHECK_ORDER"
  | "CHOOSE_PAYMENT_METHOD"
  | "CONFIGURE_PAYMENT";

export const Subscribe = withFlow(SubscribeFlow, () => {
  const paramsBasketId = useSearchParams("resourceId");
  const {
    onSuccess,
    backRoute,
    navigateToStep,
    rootRoute,
    onPageMount,
    state: { relatedServiceId },
  } = useFlow(SubscribeFlow);
  const {
    currentBasket,
    isLoading: isCurrentBasketLoading,
    setCurrentBasketId,
  } = useCurrentBasket();
  const { feature: csfBunch2, isLoading: isCsfBunch2Loading } =
    useFeatureFlag("csf_bunch_2");
  const navigate = useNavigate();

  const {
    isEnabled: isChooseFormulaPageRedesignEnabled,
    isLoading: isChooseFormulaPageRedesignLoading,
  } = useFeatureFlag("choose_formula_page_redesign");

  useEffect(() => {
    if (paramsBasketId) {
      setCurrentBasketId(paramsBasketId);
    }
  }, [paramsBasketId]);

  const handleBackToChoosePlan = () => {
    if (!isChooseFormulaPageRedesignEnabled) {
      navigateToStep("CHOOSE_PLAN");
    } else navigate(backRoute.to, backRoute);
  };

  const selectedSubscriptionProduct = useMemo(() => {
    if (isEmpty(currentBasket?.items)) {
      return undefined;
    }
    const subscriptionProduct = currentBasket?.items?.find(({ item }) =>
      isSubscriptionProduct(item)
    )?.item;

    return isSubscriptionProduct(subscriptionProduct)
      ? subscriptionProduct
      : undefined;
  }, [currentBasket?.items]);

  const handleSuccessfulCheckout = <T extends PaymentMethodType>({
    provider,
    paymentMethod,
    isPaymentConfirmed,
  }: {
    provider: IPaymentProviderForMethod[T];
    paymentMethod: T;
    isPaymentConfirmed: boolean;
  }) => {
    onSuccess({
      subscriptionPlan: {
        id: selectedSubscriptionProduct.id,
        name: selectedSubscriptionProduct.name,
        priceCents: selectedSubscriptionProduct.priceCents,
        priceCurrency: selectedSubscriptionProduct.priceCurrency,
        period: getSubscriptionPeriod(selectedSubscriptionProduct.name),
      },
      provider,
      paymentMethod,
      isPaymentConfirmed,
    });
  };

  if (
    isCurrentBasketLoading ||
    isCsfBunch2Loading ||
    isChooseFormulaPageRedesignLoading
  ) {
    return <LoadingScreen />;
  }

  return (
    <Box>
      <Routes>
        <Route
          fragments="CHOOSE_PLAN"
          matchAny
          element={<ChooseSubscriptionPlan />}
        />
        <Route
          fragments={Checkout.root}
          matchAny
          element={
            csfBunch2.enabled ? (
              <Checkout
                onLeave={handleBackToChoosePlan}
                onSuccess={handleSuccessfulCheckout}
                backRoute={rootRoute}
                onPageMount={onPageMount}
                state={{
                  paymentFormMessage: (
                    <GenericAlert status="warning">
                      <FormattedMessage
                        {...messages.serviceNotIncludedInTheSubscriptionPrice}
                      />
                    </GenericAlert>
                  ),
                  relatedServiceId,
                }}
              />
            ) : (
              <Navigate
                to={["SERVICE_OFFERS", "NEW_SUBSCRIPTION_PAYMENT"]}
                reload
                params={backRoute.params}
                queryParams={{
                  plan: "full",
                  period: selectedSubscriptionProduct?.name.replace(
                    "_subscription",
                    ""
                  ),
                }}
              />
            )
          }
        />
        <Route
          fragments="ROOT"
          element={
            <Navigate
              append
              to="CHOOSE_PLAN"
              queryParams={backRoute.queryParams}
            />
          }
        />
      </Routes>
    </Box>
  );
});
